// src/components/bridge/BridgeFormModal.tsx
import React from "react";
import { Plus, X } from "lucide-react";

export type BridgeFormMode = "bridge" | null;

export type Bridge = {
  name: string;
  comment?: string | null;
  disabled?: boolean | string;
  [key: string]: any;
};

export type ArpMode = "enabled" | "disabled";
export type YesNo = "yes" | "no";

export type BridgeFormState = {
  name: string;
  comment: string;
  arp: ArpMode;
  ageingTime: string;   // e.g. "5m"
  mtu: string;          // e.g. "1500"
  igmpSnooping: YesNo;
  dhcpSnooping: YesNo;
  vlanFiltering: YesNo;
  fastForward: YesNo;
};

interface BridgeFormModalProps {
  isOpen: boolean;
  mode: BridgeFormMode;
  onClose: () => void;

  bridgeForm: BridgeFormState;
  setBridgeForm: React.Dispatch<React.SetStateAction<BridgeFormState>>;
  bridgeFormLoading: boolean;
  handleCreateBridge: (e: React.FormEvent) => void;

  // Port props can be added later if you reuse this modal for ports.
  // Keeping them optional so they don't break anything.
  portForm?: { bridge: string; interface: string };
  setPortForm?: React.Dispatch<
    React.SetStateAction<{ bridge: string; interface: string }>
  >;
  portFormLoading?: boolean;
  handleAddPort?: (e: React.FormEvent) => void;
  bridges?: Bridge[];
}

const BridgeFormModal: React.FC<BridgeFormModalProps> = ({
  isOpen,
  mode,
  onClose,
  bridgeForm,
  setBridgeForm,
  bridgeFormLoading,
  handleCreateBridge,
}) => {
  if (!isOpen || mode !== "bridge") return null;

  const toggleYesNo = (field: keyof BridgeFormState, value: YesNo) => {
    setBridgeForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const setArp = (value: ArpMode) => {
    setBridgeForm((prev) => ({
      ...prev,
      arp: value,
    }));
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm">
      <div className="w-full max-w-xl rounded-2xl bg-white p-5 shadow-xl ring-1 ring-slate-200 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">
              Add Bridge
            </h2>
            <p className="mt-1 text-xs text-slate-500">
              Configure basic settings, network parameters and advanced
              features for this bridge.
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-full p-1 text-slate-500 hover:bg-slate-100"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleCreateBridge} className="space-y-5">
          {/* Basic Settings */}
          <section className="space-y-3 rounded-2xl border border-slate-100 bg-slate-50/60 p-4">
            <h3 className="text-sm font-semibold text-slate-900">
              Basic Settings
            </h3>

            <div className="space-y-1.5">
              <label
                htmlFor="bridge-name"
                className="text-xs font-medium text-slate-700"
              >
                Bridge Name
              </label>
              <input
                id="bridge-name"
                type="text"
                value={bridgeForm.name}
                onChange={(e) =>
                  setBridgeForm((prev) => ({ ...prev, name: e.target.value }))
                }
                placeholder="e.g. bridge-local"
                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-slate-900/70"
              />
            </div>

            <div className="space-y-1.5">
              <label
                htmlFor="bridge-comment"
                className="text-xs font-medium text-slate-700"
              >
                Comment (optional)
              </label>
              <input
                id="bridge-comment"
                type="text"
                value={bridgeForm.comment}
                onChange={(e) =>
                  setBridgeForm((prev) => ({
                    ...prev,
                    comment: e.target.value,
                  }))
                }
                placeholder="Description for this bridge"
                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-slate-900/70"
              />
            </div>
          </section>

          {/* Network Parameters */}
          <section className="space-y-3 rounded-2xl border border-slate-100 bg-slate-50/60 p-4">
            <h3 className="text-sm font-semibold text-slate-900">
              Network Parameters
            </h3>

            {/* ARP toggle */}
            <div className="space-y-2">
              <p className="text-xs font-medium text-slate-700">
                ARP (Address Resolution Protocol)
              </p>
              <div className="inline-flex rounded-full bg-slate-100 p-1 text-xs font-medium">
                <button
                  type="button"
                  onClick={() => setArp("enabled")}
                  className={`flex-1 rounded-full px-4 py-1.5 ${
                    bridgeForm.arp === "enabled"
                      ? "bg-blue-600 text-white shadow-sm"
                      : "text-slate-700"
                  }`}
                >
                  Enabled
                </button>
                <button
                  type="button"
                  onClick={() => setArp("disabled")}
                  className={`flex-1 rounded-full px-4 py-1.5 ${
                    bridgeForm.arp === "disabled"
                      ? "bg-blue-600 text-white shadow-sm"
                      : "text-slate-700"
                  }`}
                >
                  Disabled
                </button>
              </div>
            </div>

            {/* Ageing Time */}
            <div className="space-y-1.5">
              <label
                htmlFor="ageing-time"
                className="text-xs font-medium text-slate-700"
              >
                Ageing Time
              </label>
              <input
                id="ageing-time"
                type="text"
                value={bridgeForm.ageingTime}
                onChange={(e) =>
                  setBridgeForm((prev) => ({
                    ...prev,
                    ageingTime: e.target.value,
                  }))
                }
                placeholder="e.g. 5m"
                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-slate-900/70"
              />
            </div>

            {/* MTU */}
            <div className="space-y-1.5">
              <label
                htmlFor="mtu"
                className="text-xs font-medium text-slate-700"
              >
                MTU (Maximum Transmission Unit)
              </label>
              <input
                id="mtu"
                type="number"
                value={bridgeForm.mtu}
                onChange={(e) =>
                  setBridgeForm((prev) => ({
                    ...prev,
                    mtu: e.target.value,
                  }))
                }
                placeholder="1500"
                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-slate-900/70"
              />
            </div>
          </section>

          {/* Advanced Features */}
          <section className="space-y-3 rounded-2xl border border-slate-100 bg-slate-50/60 p-4">
            <h3 className="text-sm font-semibold text-slate-900">
              Advanced Features
            </h3>

            {/* IGMP Snooping */}
            <div className="flex items-center justify-between gap-3 rounded-xl bg-white px-3 py-2">
              <div>
                <p className="text-sm font-medium text-slate-900">
                  IGMP Snooping
                </p>
                <p className="text-[11px] text-slate-500">
                  Internet Group Management Protocol monitoring.
                </p>
              </div>
              <div className="inline-flex rounded-full bg-slate-100 p-1 text-xs font-medium">
                <button
                  type="button"
                  onClick={() => toggleYesNo("igmpSnooping", "yes")}
                  className={`px-3 py-1 rounded-full ${
                    bridgeForm.igmpSnooping === "yes"
                      ? "bg-blue-600 text-white shadow-sm"
                      : "text-slate-700"
                  }`}
                >
                  Enabled
                </button>
                <button
                  type="button"
                  onClick={() => toggleYesNo("igmpSnooping", "no")}
                  className={`px-3 py-1 rounded-full ${
                    bridgeForm.igmpSnooping === "no"
                      ? "bg-blue-600 text-white shadow-sm"
                      : "text-slate-700"
                  }`}
                >
                  Disabled
                </button>
              </div>
            </div>

            {/* DHCP Snooping */}
            <div className="flex items-center justify-between gap-3 rounded-xl bg-white px-3 py-2">
              <div>
                <p className="text-sm font-medium text-slate-900">
                  DHCP Snooping
                </p>
                <p className="text-[11px] text-slate-500">
                  Dynamic Host Configuration Protocol security.
                </p>
              </div>
              <div className="inline-flex rounded-full bg-slate-100 p-1 text-xs font-medium">
                <button
                  type="button"
                  onClick={() => toggleYesNo("dhcpSnooping", "yes")}
                  className={`px-3 py-1 rounded-full ${
                    bridgeForm.dhcpSnooping === "yes"
                      ? "bg-blue-600 text-white shadow-sm"
                      : "text-slate-700"
                  }`}
                >
                  Enabled
                </button>
                <button
                  type="button"
                  onClick={() => toggleYesNo("dhcpSnooping", "no")}
                  className={`px-3 py-1 rounded-full ${
                    bridgeForm.dhcpSnooping === "no"
                      ? "bg-blue-600 text-white shadow-sm"
                      : "text-slate-700"
                  }`}
                >
                  Disabled
                </button>
              </div>
            </div>

            {/* VLAN Filtering */}
            <div className="flex items-center justify-between gap-3 rounded-xl bg-white px-3 py-2">
              <div>
                <p className="text-sm font-medium text-slate-900">
                  VLAN Filtering
                </p>
                <p className="text-[11px] text-slate-500">
                  Virtual LAN traffic filtering.
                </p>
              </div>
              <div className="inline-flex rounded-full bg-slate-100 p-1 text-xs font-medium">
                <button
                  type="button"
                  onClick={() => toggleYesNo("vlanFiltering", "yes")}
                  className={`px-3 py-1 rounded-full ${
                    bridgeForm.vlanFiltering === "yes"
                      ? "bg-blue-600 text-white shadow-sm"
                      : "text-slate-700"
                  }`}
                >
                  Enabled
                </button>
                <button
                  type="button"
                  onClick={() => toggleYesNo("vlanFiltering", "no")}
                  className={`px-3 py-1 rounded-full ${
                    bridgeForm.vlanFiltering === "no"
                      ? "bg-blue-600 text-white shadow-sm"
                      : "text-slate-700"
                  }`}
                >
                  Disabled
                </button>
              </div>
            </div>

            {/* Fast Forward */}
            <div className="flex items-center justify-between gap-3 rounded-xl bg-white px-3 py-2">
              <div>
                <p className="text-sm font-medium text-slate-900">
                  Fast Forward
                </p>
                <p className="text-[11px] text-slate-500">
                  Enable hardware-accelerated forwarding.
                </p>
              </div>
              <div className="inline-flex rounded-full bg-slate-100 p-1 text-xs font-medium">
                <button
                  type="button"
                  onClick={() => toggleYesNo("fastForward", "yes")}
                  className={`px-3 py-1 rounded-full ${
                    bridgeForm.fastForward === "yes"
                      ? "bg-blue-600 text-white shadow-sm"
                      : "text-slate-700"
                  }`}
                >
                  Enabled
                </button>
                <button
                  type="button"
                  onClick={() => toggleYesNo("fastForward", "no")}
                  className={`px-3 py-1 rounded-full ${
                    bridgeForm.fastForward === "no"
                      ? "bg-blue-600 text-white shadow-sm"
                      : "text-slate-700"
                  }`}
                >
                  Disabled
                </button>
              </div>
            </div>
          </section>

          {/* Submit button */}
          <button
            type="submit"
            disabled={bridgeFormLoading}
            className="mt-1 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {bridgeFormLoading ? (
              "Creating…"
            ) : (
              <>
                <Plus className="h-4 w-4" />
                Create Bridge
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default BridgeFormModal;
